import { useState } from 'react';
import { formatCents } from '@/features/catalog/catalog';
import { MenuAcciones } from '@/ui/MenuAcciones';
import { convieneAnular, sePuedeEditar, sePuedeEliminar } from './works';
import type { WorkListRow } from './works';

/**
 * Tarjeta de un trabajo en el listado.
 *
 * **Una acción, un lugar.** Antes había cinco caminos superpuestos —tocar la
 * tarjeta, botones desplegados, el chip de estado, el menú, y un deslizamiento
 * lateral— y en el teléfono terminaban peleándose entre ellos. Lo peor no era
 * la confusión sino el deslizamiento: pasaba un trabajo a facturado sin
 * confirmación y sin que quedara claro qué lo había hecho. Un movimiento del
 * pulgar mientras se recorre una lista no puede mover plata.
 *
 * Quedaron dos caminos, y no se pisan:
 *
 * - **tocar la tarjeta** despliega lo que le pasa a la *plata* —facturado,
 *   cobrado, y cómo deshacerlos—;
 * - **el menú `⋮`** tiene lo que le pasa al *registro* —verlo, editarlo,
 *   duplicarlo, borrarlo—.
 *
 * El chip de estado es un cartel, no un botón: informa y no se toca.
 *
 * Un trabajo de período cerrado no ofrece ninguna acción. La base lo rechaza
 * igual, pero no tiene sentido invitar a algo que va a fallar.
 */
interface Props {
  work: WorkListRow;
  ocupado: boolean;
  /** Marcar facturado. Quien recibe esto confirma antes: congela el trabajo. */
  onFacturar: (id: string) => void;
  /** Marcar cobrado. No mueve ningún importe, pero también se confirma. */
  onCobrar: (id: string) => void;
  onDeshacerCobro: (id: string) => void;
  onVerDetalle: (id: string) => void;
  onEditar: (id: string) => void;
  onDuplicar: (id: string) => void;
  onEliminar: (id: string) => void;
  onAnular: (id: string) => void;
  /** Deshacer una facturación: pide motivo y queda registrado. */
  onRevertir: (id: string) => void;
  /** Color del participante que factura este trabajo. `null` = sin color. */
  colorFacturador?: string | null;
}

export function WorkCard({
  work: w,
  ocupado,
  onFacturar,
  onCobrar,
  onDeshacerCobro,
  onVerDetalle,
  onEditar,
  onDuplicar,
  onEliminar,
  onAnular,
  onRevertir,
  colorFacturador,
}: Props) {
  const [abierto, setAbierto] = useState(false);

  // El punto de no retorno es la factura. Liquidar el mes ya no congela nada:
  // entre liquidar y terminar de facturar pasan días, y en esos días aparecen
  // las correcciones.
  const editable = sePuedeEditar(w);

  return (
    <li data-testid="work-card">
      {/* Una barra al costado, no la tarjeta pintada.
          El color dice A NOMBRE DE QUIÉN SE FACTURA, y nada más: no el reparto,
          no la movilidad, no el destinatario externo. Agosto mostró que las
          cuatro pueden diferir en un mismo trabajo, así que pintar una y
          sugerir otra sería peor que no pintar nada.
          Y nunca es la única señal: el nombre de quien factura está en Ver
          detalle, y el estado sigue teniendo su propia etiqueta con texto. */}
      <div
        className="relative flex items-start gap-2 rounded-[var(--radius-superficie)] bg-[var(--superficie)] py-3 pr-2"
        style={{
          // Un acento fino, no la tarjeta pintada. Tres píxeles alcanzan para
          // agrupar de un vistazo quién factura sin que el color compita con
          // el importe, que es lo que se viene a leer.
          boxShadow: colorFacturador ? `inset 3px 0 0 0 ${colorFacturador}` : undefined,
          paddingLeft: '0.75rem',
        }}
      >
        <button
          type="button"
          onClick={() => setAbierto((v) => !v)}
          aria-expanded={abierto}
          className="min-w-0 flex-1 text-left"
          data-testid="work-open"
        >
          {/* Cliente y plata en el mismo renglón: son las dos cosas que se
              buscan, y enfrentadas se leen de un saque recorriendo la columna.
              El mes de facturación NO está acá —truncado en «factura en Agosto
              2...» no se entendía ni cuando difería del mes del trabajo—: la
              lista entera ya es de un mes, y el detalle lo dice completo. */}
          <div className="flex items-baseline justify-between gap-3">
            <p className="truncate font-semibold leading-tight">{w.client_name}</p>
            <p
              className={`shrink-0 font-semibold tabular-nums ${
                w.price_pending ? 'text-[var(--aviso)]' : ''
              }`}
              data-testid="work-total"
            >
              {formatCents(w.total_cents)}
            </p>
          </div>

          {w.establishment_name && (
            <p className="truncate text-sm text-[var(--tinta-suave)]">{w.establishment_name}</p>
          )}

          <p className="truncate text-xs text-[var(--tinta-tenue)]" data-testid="work-services">
            {new Date(`${w.work_date}T00:00:00`).toLocaleDateString('es-AR', {
              day: '2-digit',
              month: '2-digit',
            })}
            {w.service_names ? ` · ${w.service_names}` : ''}
          </p>

          {/* Los dos estados, siempre los dos, en su propio renglón.
              Antes había un solo cartel que decía en cuál de los tres estados
              estaba, y para saber si algo faltaba había que leerlo y traducir.
              Con las dos marcas a la vista se ve lo que FALTA, que es lo que se
              está buscando cuando uno recorre el mes. Y en renglón aparte
              porque compartiendo línea con el importe se apretaban los dos: a
              360 px el resumen del trabajo quedaba cortado a la mitad.
              Son carteles, no botones: sin onClick, sin role, sin foco. */}
          <p className="mt-1.5 flex gap-3 text-xs" data-testid="work-status">
            <span
              className={
                w.is_billed ? 'font-medium text-[var(--accion)]' : 'text-[var(--tinta-tenue)]'
              }
            >
              {w.is_billed ? '✓' : '○'} Facturado
            </span>
            <span
              className={
                w.is_paid ? 'font-medium text-[var(--exito)]' : 'text-[var(--tinta-tenue)]'
              }
            >
              {w.is_paid ? '✓' : '○'} Cobrado
            </span>
          </p>
        </button>

        <MenuAcciones
          etiqueta={`Acciones del trabajo de ${w.client_name}`}
          testId="work-menu"
          // Abrir el menú repliega la tarjeta: si no, el panel cae justo encima
          // de los botones desplegados y no se sabe cuál se está tocando.
          onAbrir={() => setAbierto(false)}
          acciones={[
            {
              etiqueta: 'Ver detalle',
              onClick: () => onVerDetalle(w.id),
              testId: 'menu-detalle',
            },
            ...(editable
              ? [{ etiqueta: 'Editar', onClick: () => onEditar(w.id), testId: 'menu-editar' }]
              : []),
            { etiqueta: 'Duplicar', onClick: () => onDuplicar(w.id), testId: 'menu-duplicar' },
            // Los dos, cuando los dos tienen sentido. Antes era uno O el otro:
            // en cuanto el trabajo tocaba plata, Eliminar desaparecía y no
            // quedaba forma de sacar de encima uno cargado por error. Son dos
            // intenciones distintas, no dos niveles de permiso —anular deja el
            // motivo, borrar no deja nada— y elegir es de quien carga.
            ...(convieneAnular(w)
              ? [
                  {
                    etiqueta: 'Anular',
                    onClick: () => onAnular(w.id),
                    destructiva: true,
                    testId: 'menu-anular',
                  },
                ]
              : []),
            ...(w.is_billed
              ? [
                  {
                    etiqueta: 'Revertir facturación',
                    onClick: () => onRevertir(w.id),
                    destructiva: true,
                    testId: 'menu-revertir',
                  },
                ]
              : []),
            ...(sePuedeEliminar(w)
              ? [
                  {
                    etiqueta: 'Eliminar',
                    onClick: () => onEliminar(w.id),
                    destructiva: true,
                    testId: 'menu-eliminar',
                  },
                ]
              : []),
          ]}
        />
      </div>

      {/* Desplegado: SÓLO el estado administrativo. Editar, Duplicar y Eliminar
          no están acá a propósito —viven en el menú—: tenerlos en los dos
          lugares es lo que volvía imposible saber qué iba a pasar al tocar. */}
      {/* Desplegado: SÓLO el avance administrativo.
          Cobrar está permitido aunque el trabajo esté congelado —no cambia ni
          un importe—, y deshacer la facturación NO está acá: deshace algo que
          ya salió, así que vive en el menú, pide motivo y queda registrado. */}
      {abierto && (
        <div className="flex flex-wrap gap-2 px-3 pb-3" data-testid="work-actions">
          {!editable && (
            <p className="w-full text-xs text-[var(--tinta-suave)]" data-testid="work-cerrado">
              Ya se facturó, así que sus datos económicos están congelados. Para corregirlo hay que
              revertir la facturación desde el menú.
            </p>
          )}

          {editable && (
            <button
              type="button"
              disabled={ocupado}
              onClick={() => onFacturar(w.id)}
              className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 text-sm disabled:opacity-50"
              data-testid="action-bill"
            >
              Marcar facturado
            </button>
          )}

          {w.is_billed && !w.is_paid && (
            <button
              type="button"
              disabled={ocupado}
              onClick={() => onCobrar(w.id)}
              className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 text-sm disabled:opacity-50"
              data-testid="action-collect"
            >
              Marcar cobrado
            </button>
          )}

          {w.is_paid && (
            <button
              type="button"
              disabled={ocupado}
              onClick={() => onDeshacerCobro(w.id)}
              className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 text-sm disabled:opacity-50"
              data-testid="action-undo"
            >
              Deshacer cobro
            </button>
          )}
        </div>
      )}
    </li>
  );
}
